import { useState } from "react";
import { Button, Card, FormField, Select, EmptyState, Badge } from "@/ui";
import { useT } from "@/app";
import { useCan } from "@/auth";
import { token } from "@/design/theme";
import { CatalogueTypeahead } from "@/features/catalogue/CatalogueTypeahead";
import { useParametreLaboSearch } from "@/features/catalogue/api";
import type { ParametreLaboHit } from "@/features/catalogue/types";
import { ScanOrdonnanceModal } from "./ScanOrdonnanceModal";
import { Mono } from "./Mono";
import { CAPS } from "../lib/caps";
import { formatMontant } from "../lib/format";
import type { Id, Priorite } from "../lib/types";

export interface BasketLine {
  parametre_labo_id: Id;
  /** Optional display label captured from a scan (manual entries show the raw id). */
  libelle?: string;
  prix?: number;
}

export interface BasketBuilderProps {
  lines: BasketLine[];
  priorite: Priorite;
  onPrioriteChange: (p: Priorite) => void;
  onAdd: (line: BasketLine) => void;
  onRemove: (parametreLaboId: Id) => void;
  onSeedMany: (lines: BasketLine[]) => void;
  creating: boolean;
  canCreate: boolean;
  onCreate: () => void;
}

/**
 * BasketBuilder — assembles the demande line set before creation. Two entry paths (per the server's
 * createDemande contract which accepts `parametre_labo_ids`): manual ParametreLabo-id entry (the
 * day-1 path while scan is dark) and the Rx-QR scan (seeds matched lines). Priorité routine/urgent.
 */
export function BasketBuilder({
  lines,
  priorite,
  onPrioriteChange,
  onAdd,
  onRemove,
  onSeedMany,
  creating,
  canCreate,
  onCreate,
}: BasketBuilderProps) {
  const { t } = useT();
  const canScan = useCan(CAPS.ordonnanceScan);
  // The picker gates on BOTH selecting a param (reception.parametre.select) AND the shared catalogue
  // read cap the server enforces on the search endpoint (deny-by-default; the server re-checks).
  const canSelectParam = useCan(CAPS.parametreSelect);
  const canReadCatalogue = useCan(CAPS.catalogueRead);
  const canSelect = canSelectParam && canReadCatalogue;
  const [scanOpen, setScanOpen] = useState(false);

  const addHit = (hit: ParametreLaboHit) => {
    // Skip a duplicate line; otherwise add the priced overlay (id + libellé + prix the demande needs).
    if (lines.some((l) => String(l.parametre_labo_id) === String(hit.parametreLaboId))) return;
    onAdd({ parametre_labo_id: hit.parametreLaboId, libelle: hit.libelle, prix: hit.prix });
  };

  const prioriteOptions: Array<{ value: Priorite; label: string }> = [
    { value: "routine", label: t("reception.demande.priorite.routine") },
    { value: "urgent", label: t("reception.demande.priorite.urgent") },
  ];

  return (
    <Card title={t("reception.demande.title")}>
      <div style={{ display: "flex", flexDirection: "column", gap: token.s4 }}>
        <div
          style={{
            display: "flex",
            gap: token.s4,
            alignItems: "flex-end",
            flexWrap: "wrap",
          }}
        >
          <div style={{ flex: "1 1 280px" }}>
            <FormField
              label={t("reception.demande.addParam")}
              hint={t("reception.demande.addParamHint")}
            >
              {() => (
                <CatalogueTypeahead<ParametreLaboHit>
                  useSearch={useParametreLaboSearch}
                  disabled={!canSelect}
                  placeholder={t("reception.demande.addParamPlaceholder")}
                  aria-label={t("reception.demande.addParam")}
                  getKey={(r) => String(r.parametreLaboId)}
                  onSelect={addHit}
                  renderItem={(r) => (
                    <>
                      <span style={{ flex: "1 1 auto" }}>{r.libelle}</span>
                      <Mono>{r.parametreCode}</Mono>
                      <Badge tone="neutral">
                        <Mono>{formatMontant(r.prix)}</Mono>
                      </Badge>
                    </>
                  )}
                />
              )}
            </FormField>
          </div>

          <FormField label={t("reception.demande.priorite")}>
            {(p) => (
              <Select
                {...p}
                value={priorite}
                onChange={(e) => onPrioriteChange(e.target.value as Priorite)}
                options={prioriteOptions}
              />
            )}
          </FormField>

          {canScan && (
            <Button variant="secondary" onClick={() => setScanOpen(true)} leadingIcon={<span aria-hidden>▣</span>}>
              {t("reception.scan.title")}
            </Button>
          )}
        </div>

        {lines.length === 0 ? (
          <EmptyState
            glyph="▤"
            title={t("reception.demande.basket.empty")}
            description={t("reception.demande.basket.emptyDesc")}
          />
        ) : (
          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: token.s2 }}>
            {lines.map((l) => (
              <li
                key={String(l.parametre_labo_id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: token.s3,
                  padding: token.s2,
                  border: `1px solid ${token.lineSoft}`,
                  borderRadius: token.rSm,
                }}
              >
                <Mono>#{String(l.parametre_labo_id)}</Mono>
                <span style={{ flex: "1 1 auto" }}>{l.libelle ?? ""}</span>
                {l.prix != null && (
                  <Badge tone="neutral">
                    <Mono>{formatMontant(l.prix)}</Mono>
                  </Badge>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={`Retirer ${String(l.parametre_labo_id)}`}
                  onClick={() => onRemove(l.parametre_labo_id)}
                >
                  ×
                </Button>
              </li>
            ))}
          </ul>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button onClick={onCreate} loading={creating} disabled={!canCreate || lines.length === 0}>
            {t("reception.demande.create")}
          </Button>
        </div>
      </div>

      <ScanOrdonnanceModal
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        onSeed={(_ids, result) =>
          onSeedMany(
            result.lignes.map((rl) => ({
              parametre_labo_id: rl.parametre_labo_id,
              libelle: rl.libelle,
              prix: rl.prix,
            })),
          )
        }
      />
    </Card>
  );
}
